/**
 * ProjectWizardView — durchgängiger Vollbild-Assistent für den neuen Staging-Flow.
 *
 * Schritte:
 *  ① Hochladen           — Projektname (bei mode 'new'), Fotos per Drag&Drop / Auswahl, Mieter-Link
 *  ② Einstellen          — Format (projektweit), Raumtyp pro Foto, „Küche behalten"
 *  ③ Prüfen & Einrichten — Empty-first-Generierung (2 Varianten) und Staging pro Raum in einer
 *                           Ansicht: Original · Leer (A/B-Wahl) · Eingerichtet; Stil global im Kopf
 *  ④ Branding & Download — Logo, Overlay, Wasserzeichen + Live-Vorschau und ZIP-Export
 *
 * Pricing: Leeren 1 Credit/Raum · Einrichten 1 Credit/Raum · Anpassen/Nachbessern 0,5 Credits
 * (admin-editierbar via credit_pricing; Modal-Buttons zeigen den Live-Preis).
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignStartVertical,
  ArrowLeft,
  ArrowRight,
  Camera,
  Check,
  CloudUpload,
  Coins,
  CornerLeftDown,
  CornerLeftUp,
  CornerRightDown,
  CornerRightUp,
  Download,
  Droplets,
  Grid3X3,
  Image as ImageIcon,
  Info,
  Link2,
  Loader2,
  Pencil,
  RectangleHorizontal,
  RefreshCw,
  Save,
  Sparkles,
  Square,
  Upload,
  Wand2,
  X,
} from 'lucide-react';
import { saveAs } from 'file-saver';
import { assembleExportZip, planExportFiles, exportZipFileName, type ExportEntry } from '@/services/exportService';

import { Card, CardContent } from '@/components/ui/card';
import { AiLabelOption, useAiLabelPrefs } from '@/components/ui/AiLabelOption';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Slider } from '@/components/ui/slider';
import { TimedProgress } from '@/components/ui/TimedProgress';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import HeicSafeImage from '@/components/ui/HeicSafeImage';
import { ImageInspectorModal, InspectorRoom } from '@/components/ui/ImageInspectorModal';

import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  createProject,
  updateProject,
  uploadProjectImage,
  updateImageMetadata,
  deleteProjectImage,
  fetchProjectImages,
  fetchImageHistoryEntries,
  getPublicUrl,
} from '@/services/projectService';
import { generateRoomImage } from '@/services/geminiService';
import { fetchBrandingAssets, uploadBrandingAsset, uploadOverlayAsset } from '@/services/brandingService';
import { updateDefaultBrandingDefaults, fetchUserProfile } from '@/services/paymentService';
import { applyAiLabelToImage, applyBrandingDetailed, BrandingLayerError, processWatermark, renderBrandedExport, uploadBrandedImagesToSupabase } from '@/services/imageProcessing';
import type { BrandingOptions, WatermarkOptions } from '@/services/imageProcessing';
import {
  Project,
  RoomType,
  GenerationType,
  AspectRatio,
  StagingStyle,
  BrandingAsset,
  DbImage,
  LogoPosition,
  WatermarkPosition,
} from '@/types';
import {
  getIndoorRooms,
  getOutdoorRooms,
  getRoomLabel,
  supportsStaging,
  STAGING_STYLES,
} from '@/lib/roomConfig';
import { cn, formatCredits } from '@/lib/utils';
import { overlaysForSelection, resolveOverlayUrlForAspect, overlayNeedsAspectWarning, ASPECT_VALUE } from '@/lib/overlayVariants';
import { useUnloadWarning } from '@/hooks/useUnloadWarning';
import {
  loadWizardSession,
  saveWizardSession,
  clearWizardSession,
} from '@/lib/wizardSession';

// ============================================================================
// Props contract (App.tsx is wired against these exactly)
// ============================================================================

export type WizardMode = 'new' | 'add';

export interface ProjectWizardViewProps {
  mode: WizardMode;
  project: Project | null; // null for mode 'new' until created
  isAdmin: boolean;
  credits: number; // total image credits
  onCreditsRefresh: () => void;
  onProjectCreated: (p: Project) => void;
  onExit: (dest: 'workspace' | 'dashboard', projectId?: string) => void;
  /** Meldet laufende Generierungen nach oben (App sperrt/​warnt bei Sidebar-Navigation). */
  onBusyChange?: (busy: boolean) => void;
}

// ============================================================================
// Internal types & constants
// ============================================================================

const CONCURRENCY_LIMIT = 2;
const MAX_PHOTOS = 20;

type WizardStep = 1 | 2 | 3 | 4;
type GenStatus = 'idle' | 'queued' | 'running' | 'done' | 'error';

/** Eine Leer-Variante (A/B) aus der Empty-first-Generierung. */
interface EmptyVariant {
  imageUrl: string;
  storagePath: string;
}

interface WizardRoom {
  localId: string;
  dbId?: string;
  file?: File;
  previewUrl: string;
  storagePath?: string;
  roomType: RoomType;
  customLabel?: string;
  keepKitchen: boolean;
  isUploading: boolean;
  uploadError?: string;
  // Schritt 3: Leeren
  emptyStatus: GenStatus;
  emptyError?: string;
  emptyUrl?: string; // aktive (gewählte) Leer-Variante
  emptyPath?: string;
  emptyVariants?: EmptyVariant[]; // alle A/B-Varianten zur Auswahl unter dem Ergebnis
  // Schritt 3: Einrichten
  stagedStatus: GenStatus;
  stagedError?: string;
  stagedUrl?: string;
  stagedPath?: string;
}

const STEP_LABELS: Record<WizardStep, string> = {
  1: 'Hochladen',
  2: 'Einstellen',
  3: 'Prüfen & Einrichten',
  4: 'Branding & Download',
};

/**
 * Baut aus einer DB-Bildzeile (+ Empty-Historie) einen WizardRoom — für den
 * Wiedereintritt in den Assistenten (Session-Resume). Original/Leer/Eingerichtet
 * werden aus den gespeicherten Storage-Pfaden abgeleitet.
 */
const dbImageToWizardRoom = (
  db: DbImage,
  emptyVariants: EmptyVariant[]
): WizardRoom => ({
  localId: `wiz-db-${db.id}`,
  dbId: db.id,
  previewUrl: db.storage_path ? getPublicUrl(db.storage_path) : '',
  storagePath: db.storage_path,
  roomType: (db.room_type as RoomType) ?? RoomType.LIVING,
  customLabel: db.custom_label,
  keepKitchen: !!db.keep_kitchen,
  isUploading: false,
  emptyStatus: db.storage_path_empty ? 'done' : 'idle',
  emptyUrl: db.storage_path_empty ? getPublicUrl(db.storage_path_empty) : undefined,
  emptyPath: db.storage_path_empty,
  emptyVariants: emptyVariants.length ? emptyVariants : undefined,
  stagedStatus: db.storage_path_staged ? 'done' : 'idle',
  stagedUrl: db.storage_path_staged ? getPublicUrl(db.storage_path_staged) : undefined,
  stagedPath: db.storage_path_staged,
});

const roomDisplayName = (room: Pick<WizardRoom, 'roomType' | 'customLabel'>): string =>
  room.roomType === RoomType.CUSTOM && room.customLabel?.trim()
    ? room.customLabel.trim()
    : getRoomLabel(room.roomType);

const isImageFile = (file: File): boolean =>
  file.type.startsWith('image/') || /\.(heic|heif)$/i.test(file.name);

/** Throttled parallel execution (bestehendes CONCURRENCY_LIMIT-Muster aus App.tsx). */
const runWithConcurrency = async (tasks: Array<() => Promise<void>>): Promise<void> => {
  const active = new Set<Promise<void>>();
  for (const task of tasks) {
    if (active.size >= CONCURRENCY_LIMIT) {
      await Promise.race(active);
    }
    const promise = task();
    active.add(promise);
    void promise.finally(() => active.delete(promise));
  }
  await Promise.all(active);
};

// ============================================================================
// Component
// ============================================================================

export const ProjectWizardView: React.FC<ProjectWizardViewProps> = ({
  mode,
  project: projectProp,
  isAdmin,
  credits,
  onCreditsRefresh,
  onProjectCreated,
  onExit,
  onBusyChange,
}) => {
  const { user } = useAuth();

  const [step, setStep] = useState<WizardStep>(1);
  const [project, setProject] = useState<Project | null>(projectProp);
  const [projectName, setProjectName] = useState<string>(projectProp?.name ?? '');
  const [rooms, setRooms] = useState<WizardRoom[]>([]);
  const [aspect, setAspect] = useState<AspectRatio>(projectProp?.aspectRatio ?? '4:3');
  const [style, setStyle] = useState<StagingStyle>(projectProp?.stagingStyle ?? 'modern');

  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [linkState, setLinkState] = useState<'idle' | 'busy' | 'copied' | 'error'>('idle');

  // Prüf-Popup (geteiltes Inspector-Modal)
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [inspectorIndex, setInspectorIndex] = useState(0);

  // Schritt 4 — Branding & Download
  const [overlayAssets, setOverlayAssets] = useState<BrandingAsset[] | null>(null);
  const [logoAssets, setLogoAssets] = useState<BrandingAsset[] | null>(null);
  const [watermarkAssets, setWatermarkAssets] = useState<BrandingAsset[] | null>(null);
  const [overlayChoice, setOverlayChoice] = useState<'none' | 'own'>('none');
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null);
  const [defaultOverlayId, setDefaultOverlayId] = useState<string | null>(null);
  const [selectedLogoId, setSelectedLogoId] = useState<string | null>(null);
  const [logoPosition, setLogoPosition] = useState<LogoPosition>('bottom-right');
  const [selectedWatermarkId, setSelectedWatermarkId] = useState<string | null>(null);
  const [watermarkOpacity, setWatermarkOpacity] = useState(30); // %
  const [watermarkScale, setWatermarkScale] = useState(0.6); // 0.1–1
  const [watermarkPosition, setWatermarkPosition] = useState<WatermarkPosition>('center');
  const [watermarkPreviewUrl, setWatermarkPreviewUrl] = useState<string | null>(null);
  const [uploadingAssetKind, setUploadingAssetKind] = useState<'logo' | 'overlay' | 'watermark' | null>(null);
  const [exportCompressed, setExportCompressed] = useState(false);
  const aiLabel = useAiLabelPrefs();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSavingBranding, setIsSavingBranding] = useState(false);
  const brandedPersistedRef = useRef(false);
  const savingBrandingRef = useRef(false);

  // Aktuellster State für async Abläufe (Uploads → Weiter)
  const roomsRef = useRef<WizardRoom[]>(rooms);
  useEffect(() => {
    roomsRef.current = rooms;
  }, [rooms]);

  // Falls App.tsx das Projekt nachträglich liefert (z. B. mode 'add' async geladen)
  useEffect(() => {
    if (projectProp && !project) {
      setProject(projectProp);
      setProjectName(projectProp.name);
      setAspect(projectProp.aspectRatio ?? '4:3');
      setStyle(projectProp.stagingStyle ?? 'modern');
    }
  }, [projectProp, project]);

  const patchRoom = useCallback((localId: string, updates: Partial<WizardRoom>) => {
    setRooms((prev) => prev.map((r) => (r.localId === localId ? { ...r, ...updates } : r)));
  }, []);

  // ==========================================================================
  // Session-Persistenz (Fortsetzen nach Weg-Navigieren / Reload)
  // ==========================================================================

  // Gate: Persistenz erst NACH dem Rekonstruktions-Versuch aktivieren, sonst
  // überschreibt der erste Render (rooms leer, Schritt 1) die gespeicherte
  // Session, bevor sie gelesen wurde.
  const hydratedRef = useRef(false);

  // True, wenn eine gespeicherte Session wiederhergestellt wurde — deren
  // Overlay-Wahl darf der Asset-Autoselect nicht überschreiben.
  const sessionRestoredRef = useRef(false);

  // Wiedereintritt: liegt für dieses Projekt eine Session vor, den Raum-Batch
  // aus der DB rekonstruieren und die UI-Position wiederherstellen. Läuft nur,
  // solange rooms leer ist (kein Eingriff in einen aktiven mode-'new'-Durchlauf).
  useEffect(() => {
    if (hydratedRef.current || !project) return;
    let cancelled = false;

    void (async () => {
      const session = loadWizardSession(project.id);
      if (session && session.roomImageIds.length > 0 && roomsRef.current.length === 0) {
        try {
          const dbImages = await fetchProjectImages(project.id);
          const byId = new Map(dbImages.map((d) => [d.id, d]));
          const rebuilt: WizardRoom[] = [];
          for (const imgId of session.roomImageIds) {
            const db = byId.get(imgId);
            if (!db) continue; // im Workspace gelöscht → überspringen
            let emptyVariants: EmptyVariant[] = [];
            try {
              const hist = await fetchImageHistoryEntries(imgId);
              emptyVariants = hist
                .filter((e) => e.version_type === 'Empty')
                .map((e) => ({ imageUrl: getPublicUrl(e.storage_path), storagePath: e.storage_path }));
            } catch {
              // Verlauf nicht ladbar → nur die aktive Leer-Variante (im Mapper ergänzt)
            }
            rebuilt.push(dbImageToWizardRoom(db, emptyVariants));
          }
          if (!cancelled && rebuilt.length > 0) {
            sessionRestoredRef.current = true;
            setRooms(rebuilt);
            setStep(session.step);
            setStyle(session.style);
            setAspect(session.aspect);
            setOverlayChoice(session.overlayChoice);
            setExportCompressed(session.exportCompressed);
            setSelectedOverlayId(session.selectedOverlayId ?? null);
            setSelectedLogoId(session.brandingLogoAssetId ?? null);
            if (session.brandingLogoPosition) setLogoPosition(session.brandingLogoPosition);
            setSelectedWatermarkId(session.brandingWatermarkAssetId ?? null);
            if (typeof session.brandingWatermarkOpacity === 'number') setWatermarkOpacity(session.brandingWatermarkOpacity);
            if (typeof session.brandingWatermarkScale === 'number') setWatermarkScale(session.brandingWatermarkScale);
            if (session.brandingWatermarkPosition) setWatermarkPosition(session.brandingWatermarkPosition);
          }
        } catch (err) {
          console.error('[Wizard] Session-Rekonstruktion fehlgeschlagen:', err);
        }
      }
      if (!cancelled) hydratedRef.current = true;
    })();

    return () => {
      cancelled = true;
    };
  }, [project]);

  // Fortschritt speichern (erst nach Hydration). Schreibt eine schlanke Momentaufnahme:
  // UI-Position + Zugehörigkeit der DB-Bilder zum Durchlauf.
  useEffect(() => {
    if (!hydratedRef.current || !project) return;
    const dbRooms = rooms.filter((r) => r.dbId);
    saveWizardSession({
      projectId: project.id,
      step,
      style,
      aspect,
      overlayChoice,
      exportCompressed,
      roomImageIds: dbRooms.map((r) => r.dbId as string),
      selectedOverlayId,
      brandingLogoAssetId: selectedLogoId,
      brandingLogoPosition: logoPosition,
      brandingWatermarkAssetId: selectedWatermarkId,
      brandingWatermarkOpacity: watermarkOpacity,
      brandingWatermarkScale: watermarkScale,
      brandingWatermarkPosition: watermarkPosition,
    });
  }, [project, step, style, aspect, overlayChoice, exportCompressed, rooms, selectedOverlayId, selectedLogoId, logoPosition, selectedWatermarkId, watermarkOpacity, watermarkScale, watermarkPosition]);

  // ==========================================================================
  // Schritt 1 — Hochladen
  // ==========================================================================

  /**
   * Ein Upload-Promise je Foto: verhindert Doppel-Uploads (Sofort-Upload vs.
   * „Weiter") und macht das Warten auf laufende Uploads race-frei — der State
   * (roomsRef) kann direkt nach einem await noch veraltet sein.
   */
  const uploadPromisesRef = useRef<Map<string, Promise<boolean>>>(new Map());

  const uploadRoom = useCallback(
    (projectId: string, room: WizardRoom): Promise<boolean> => {
      const existing = uploadPromisesRef.current.get(room.localId);
      if (existing) return existing;

      const promise = (async (): Promise<boolean> => {
        if (!room.file) return false;
        patchRoom(room.localId, { isUploading: true, uploadError: undefined });
        try {
          const db = await uploadProjectImage(projectId, room.file, room.roomType, room.keepKitchen, room.customLabel);
          patchRoom(room.localId, { dbId: db.id, storagePath: db.storage_path, isUploading: false });
          return true;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Upload fehlgeschlagen';
          console.error('[Wizard] Upload failed:', err);
          patchRoom(room.localId, { isUploading: false, uploadError: message });
          // Fehlgeschlagene Uploads aus dem Register nehmen → „Weiter" versucht es erneut
          uploadPromisesRef.current.delete(room.localId);
          return false;
        }
      })();

      uploadPromisesRef.current.set(room.localId, promise);
      return promise;
    },
    [patchRoom]
  );

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      setNotice(null);
      const valid = files.filter(isImageFile);
      const remaining = MAX_PHOTOS - roomsRef.current.length;
      if (valid.length > remaining) {
        setNotice(`Maximal ${MAX_PHOTOS} Fotos pro Durchlauf — ${valid.length - remaining} Foto(s) wurden nicht übernommen.`);
      }
      const accepted = valid.slice(0, Math.max(0, remaining));
      if (accepted.length === 0) return;

      const newRooms: WizardRoom[] = accepted.map((file) => ({
        localId: `wiz-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        previewUrl: URL.createObjectURL(file),
        roomType: RoomType.LIVING,
        keepKitchen: false,
        isUploading: false,
        emptyStatus: 'idle',
        stagedStatus: 'idle',
      }));
      setRooms((prev) => [...prev, ...newRooms]);

      // Projekt existiert bereits → sofort hochladen (HEIC-Konvertierung passiert im Service)
      const proj = project;
      if (proj) {
        newRooms.forEach((room) => void uploadRoom(proj.id, room));
      }
    },
    [project, uploadRoom]
  );

  const handleRemoveRoom = useCallback(
    async (room: WizardRoom) => {
      setRooms((prev) => prev.filter((r) => r.localId !== room.localId));
      uploadPromisesRef.current.delete(room.localId);
      if (room.previewUrl.startsWith('blob:')) URL.revokeObjectURL(room.previewUrl);
      if (room.dbId) {
        try {
          await deleteProjectImage(room.dbId, [room.storagePath].filter(Boolean) as string[]);
        } catch (err) {
          console.error('[Wizard] Failed to delete image:', err);
        }
      }
    },
    []
  );

  /** Erstellt das Projekt bei Bedarf (mode 'new'), sonst gibt es das bestehende zurück. */
  const ensureProject = useCallback(async (): Promise<Project | null> => {
    if (project) return project;
    if (!user?.id) {
      setNotice('Keine aktive Sitzung — bitte neu anmelden.');
      return null;
    }
    const name = projectName.trim();
    if (!name) {
      setNotice('Bitte gib zuerst einen Projektnamen ein.');
      return null;
    }
    try {
      const created = await createProject(user.id, name, '', aspect, style);
      setProject(created);
      onProjectCreated(created);
      return created;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Projekt konnte nicht erstellt werden';
      console.error('[Wizard] createProject failed:', err);
      setNotice(message);
      return null;
    }
  }, [project, user?.id, projectName, aspect, style, onProjectCreated]);

  const handleCopyTenantLink = useCallback(async () => {
    if (linkState === 'busy') return;
    setLinkState('busy');
    try {
      const proj = await ensureProject();
      if (!proj) {
        setLinkState('idle');
        return;
      }
      let token = proj.uploadToken;
      if (!token) {
        token = typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        await updateProject(proj.id, { upload_token: token });
        setProject((prev) => (prev ? { ...prev, uploadToken: token } : prev));
      }
      await navigator.clipboard.writeText(`${window.location.origin}/upload/${token}`);
      setLinkState('copied');
      window.setTimeout(() => setLinkState('idle'), 4000);
    } catch (err) {
      console.error('[Wizard] Tenant link copy failed:', err);
      setLinkState('error');
      window.setTimeout(() => setLinkState('idle'), 4000);
    }
  }, [ensureProject, linkState]);

  const handleStep1Next = useCallback(async () => {
    setNotice(null);
    setIsAdvancing(true);
    try {
      const proj = await ensureProject();
      if (!proj) return;

      // Neue/fehlgeschlagene Uploads starten + auf bereits laufende warten
      // (uploadRoom dedupliziert über das Promise-Register).
      const targets = roomsRef.current.filter((r) => r.file && !r.dbId);
      const results = await Promise.all(targets.map((room) => uploadRoom(proj.id, room)));
      if (results.some((ok) => !ok)) {
        setNotice('Einige Fotos konnten nicht hochgeladen werden. Entferne sie oder versuche es mit „Weiter" erneut.');
        return;
      }
      setStep(2);
    } finally {
      setIsAdvancing(false);
    }
  }, [ensureProject, uploadRoom]);

  // Drag & Drop
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  }, []);
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;
      handleFilesSelected(Array.from(e.dataTransfer.files));
    },
    [handleFilesSelected]
  );

  // ==========================================================================
  // Schritt 2 — Einstellen
  // ==========================================================================

  const handleAspectChange = useCallback(
    (value: AspectRatio) => {
      setAspect(value);
      if (project) {
        updateProject(project.id, { aspect_ratio: value }).catch((err) =>
          console.error('[Wizard] aspect_ratio update failed:', err)
        );
      }
    },
    [project]
  );

  const handleRoomTypeChange = useCallback(
    (room: WizardRoom, type: RoomType) => {
      patchRoom(room.localId, { roomType: type });
      if (room.dbId) {
        updateImageMetadata(room.dbId, { room_type: type }).catch((err) =>
          console.error('[Wizard] room_type update failed:', err)
        );
      }
    },
    [patchRoom]
  );

  const handleCustomLabelChange = useCallback(
    (room: WizardRoom, label: string) => {
      patchRoom(room.localId, { customLabel: label });
      if (room.dbId) {
        updateImageMetadata(room.dbId, { custom_label: label }).catch((err) =>
          console.error('[Wizard] custom_label update failed:', err)
        );
      }
    },
    [patchRoom]
  );

  const handleKeepKitchenChange = useCallback(
    (room: WizardRoom, keep: boolean) => {
      patchRoom(room.localId, { keepKitchen: keep });
      if (room.dbId) {
        updateImageMetadata(room.dbId, { keep_kitchen: keep }).catch((err) =>
          console.error('[Wizard] keep_kitchen update failed:', err)
        );
      }
    },
    [patchRoom]
  );

  // ==========================================================================
  // Schritt 3 (Prüfen & Einrichten) — Generierung (Leeren + Staging)
  // ==========================================================================

  const runEmptyForRoom = useCallback(
    async (room: WizardRoom) => {
      const proj = project;
      if (!proj || !room.storagePath) return;
      patchRoom(room.localId, { emptyStatus: 'running', emptyError: undefined });
      try {
        const res = await generateRoomImage(
          room.storagePath,
          proj.id,
          room.roomType,
          GenerationType.EMPTY,
          room.keepKitchen,
          undefined,
          room.customLabel,
          room.dbId,
          undefined,
          user?.id,
          aspect,
          style,
          undefined,
          2 // 2 Leer-Varianten (A/B) zur Auswahl unter dem Ergebnis
        );
        const variants: EmptyVariant[] = (res.variants ?? []).map((v) => ({
          imageUrl: v.imageUrl,
          storagePath: v.storagePath,
        }));
        if (variants.length === 0 && res.imageUrl && res.storagePath) {
          variants.push({ imageUrl: res.imageUrl, storagePath: res.storagePath });
        }
        const first = variants[0];
        if (!first) throw new Error(res.error || 'Generierung fehlgeschlagen');
        patchRoom(room.localId, {
          emptyStatus: 'done',
          emptyUrl: first.imageUrl,
          emptyPath: first.storagePath,
          emptyVariants: variants,
        });
        // Variante A ist vorausgewählt → sofort als gewähltes Leerbild persistieren
        // (gleicher Mechanismus wie beim Umschalten der Variante in Schritt 3).
        if (room.dbId) {
          const { error } = await supabase
            .from('images')
            .update({ storage_path_empty: first.storagePath })
            .eq('id', room.dbId);
          if (error) console.error('[Wizard] Default empty variant persist failed:', error);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Generierung fehlgeschlagen';
        console.error('[Wizard] Empty generation failed:', err);
        patchRoom(room.localId, { emptyStatus: 'error', emptyError: message });
      } finally {
        onCreditsRefresh();
      }
    },
    [project, patchRoom, user?.id, aspect, style, onCreditsRefresh]
  );

  /** Persistiert die vom Nutzer gewählte A/B-Leer-Variante (Basis fürs Einrichten). */
  const handleSelectEmptyVariant = useCallback(
    async (room: WizardRoom, variant: EmptyVariant) => {
      if (room.emptyPath === variant.storagePath) return;
      patchRoom(room.localId, { emptyUrl: variant.imageUrl, emptyPath: variant.storagePath });
      if (room.dbId) {
        const { error } = await supabase
          .from('images')
          .update({ storage_path_empty: variant.storagePath })
          .eq('id', room.dbId);
        if (error) console.error('[Wizard] Empty variant persist failed:', error);
      }
    },
    [patchRoom]
  );

  const handleStartEmptying = useCallback(async () => {
    setNotice(null);
    setStep(3);
    const targets = roomsRef.current.filter((r) => r.emptyStatus === 'idle' && r.storagePath);
    await runWithConcurrency(targets.map((room) => () => runEmptyForRoom(room)));
  }, [runEmptyForRoom]);

  const runStagedForRoom = useCallback(
    async (room: WizardRoom) => {
      const proj = project;
      if (!proj || !room.storagePath || !room.emptyPath) return;
      patchRoom(room.localId, { stagedStatus: 'running', stagedError: undefined });
      try {
        // Gewählte Leer-Variante wurde bereits bei der Auswahl persistiert —
        // hier idempotent absichern: Staging baut immer auf dem freigegebenen
        // Leerbild auf (Empty-first, vgl. handleStageApproved in App.tsx).
        if (room.dbId) {
          await supabase.from('images').update({ storage_path_empty: room.emptyPath }).eq('id', room.dbId);
        }
        const res = await generateRoomImage(
          room.storagePath,
          proj.id,
          room.roomType,
          GenerationType.STAGED,
          room.keepKitchen,
          undefined,
          room.customLabel,
          room.dbId,
          undefined,
          user?.id,
          aspect,
          style,
          undefined,
          1,
          room.emptyPath
        );
        const url = res.variants?.[0]?.imageUrl ?? res.imageUrl;
        const path = res.variants?.[0]?.storagePath ?? res.storagePath;
        if (!url || !path) throw new Error(res.error || 'Generierung fehlgeschlagen');
        patchRoom(room.localId, { stagedStatus: 'done', stagedUrl: url, stagedPath: path });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Generierung fehlgeschlagen';
        console.error('[Wizard] Staged generation failed:', err);
        patchRoom(room.localId, { stagedStatus: 'error', stagedError: message });
      } finally {
        onCreditsRefresh();
      }
    },
    [project, patchRoom, user?.id, aspect, style, onCreditsRefresh]
  );

  /** Stil gilt global für alle Räume — Änderung sofort am Projekt persistieren. */
  const handleStyleChange = useCallback(
    (value: StagingStyle) => {
      setStyle(value);
      if (project) {
        updateProject(project.id, { staging_style: value }).catch((err) =>
          console.error('[Wizard] staging_style update failed:', err)
        );
      }
    },
    [project]
  );

  /** Staging für EINEN Raum direkt aus der Karte anstoßen (1 Credit). */
  const handleStageRoom = useCallback(
    async (room: WizardRoom) => {
      if (room.stagedStatus === 'queued' || room.stagedStatus === 'running') return;
      patchRoom(room.localId, { stagedStatus: 'queued', stagedError: undefined });
      await runStagedForRoom(room);
    },
    [patchRoom, runStagedForRoom]
  );

  // ==========================================================================
  // Prüf-Popup (geteiltes Inspector-Modal)
  // ==========================================================================

  const inspectorRooms: InspectorRoom[] = useMemo(
    () =>
      rooms
        .filter((r): r is WizardRoom & { dbId: string } => !!r.dbId)
        .map((r) => ({
          imageId: r.dbId,
          name: roomDisplayName(r),
          roomType: r.roomType,
          keepKitchen: r.keepKitchen,
          originalUrl: r.storagePath ? getPublicUrl(r.storagePath) : null,
          originalPath: r.storagePath ?? null,
        })),
    [rooms]
  );

  const openInspectorFor = useCallback(
    (room: WizardRoom) => {
      if (!room.dbId) return;
      const idx = inspectorRooms.findIndex((r) => r.imageId === room.dbId);
      if (idx < 0) return;
      setInspectorIndex(idx);
      setInspectorOpen(true);
    },
    [inspectorRooms]
  );

  /**
   * Nach Änderungen im Popup (Variante gewählt, nachgebessert, gelöscht): DB-Stand übernehmen.
   * Die Leer-Varianten werden aus image_history neu aufgebaut — dieselbe Quelle und
   * Reihenfolge wie im Popup, damit Strip und Popup nie auseinanderlaufen.
   */
  const handleInspectorChanged = useCallback(async () => {
    const proj = project;
    if (!proj) return;
    try {
      const dbImages = await fetchProjectImages(proj.id);
      const historyByImage = new Map<string, EmptyVariant[]>();
      await Promise.all(
        dbImages.map(async (db) => {
          try {
            const entries = await fetchImageHistoryEntries(db.id);
            historyByImage.set(
              db.id,
              entries
                .filter((e) => e.version_type === 'Empty')
                .map((e) => ({ imageUrl: getPublicUrl(e.storage_path), storagePath: e.storage_path }))
            );
          } catch {
            // Verlauf nicht ladbar → Varianten dieses Raums unverändert lassen
          }
        })
      );
      setRooms((prev) =>
        prev
          .filter((r) => !r.dbId || dbImages.some((d) => d.id === r.dbId))
          .map((r) => {
            const db = dbImages.find((d) => d.id === r.dbId);
            if (!db) return r;
            // Varianten = Empty-Historie (wie im Popup); aktuelles storage_path_empty
            // ergänzen, falls es dort fehlt (Altbestand vor dem History-Fix).
            let emptyVariants = historyByImage.get(db.id) ?? r.emptyVariants;
            if (
              db.storage_path_empty &&
              emptyVariants &&
              !emptyVariants.some((v) => v.storagePath === db.storage_path_empty)
            ) {
              emptyVariants = [
                ...emptyVariants,
                { imageUrl: getPublicUrl(db.storage_path_empty), storagePath: db.storage_path_empty },
              ];
            }
            return {
              ...r,
              roomType: (db.room_type as RoomType) ?? r.roomType,
              customLabel: db.custom_label ?? r.customLabel,
              keepKitchen: db.keep_kitchen ?? r.keepKitchen,
              emptyVariants,
              emptyPath: db.storage_path_empty ?? r.emptyPath,
              emptyUrl: db.storage_path_empty ? getPublicUrl(db.storage_path_empty) : r.emptyUrl,
              emptyStatus: db.storage_path_empty ? 'done' : r.emptyStatus,
              stagedPath: db.storage_path_staged ?? r.stagedPath,
              stagedUrl: db.storage_path_staged ? getPublicUrl(db.storage_path_staged) : r.stagedUrl,
              stagedStatus: db.storage_path_staged ? 'done' : r.stagedStatus,
            };
          })
      );
    } catch (err) {
      console.error('[Wizard] Refresh after inspector change failed:', err);
    }
  }, [project]);

  // ==========================================================================
  // Schritt 4 — Branding & Download
  // ==========================================================================

  // Assets laden, sobald Schritt 4 betreten wird (einmalig pro Mount).
  useEffect(() => {
    if (step !== 4 || overlayAssets !== null || !user?.id) return;
    const uid = user.id;
    void Promise.all([
      fetchBrandingAssets(uid, 'overlay'),
      fetchBrandingAssets(uid, 'logo'),
      fetchBrandingAssets(uid, 'watermark'),
      fetchUserProfile(uid).catch(() => null),
    ])
      .then(([ovl, logos, wms, profile]) => {
        const defId = profile?.defaultOverlayAssetId ?? null;
        setDefaultOverlayId(defId);
        setOverlayAssets(ovl);
        setLogoAssets(logos);
        setWatermarkAssets(wms);
        if (ovl.length > 0 && !sessionRestoredRef.current) {
          // Standard-Overlay vorauswählen, sonst die erste Auswahl-Variante (4:3).
          const primaries = overlaysForSelection(ovl);
          const preId = defId && ovl.some((o) => o.id === defId) ? defId : (primaries[0]?.id ?? ovl[0].id);
          setOverlayChoice('own');
          setSelectedOverlayId((prev) => prev ?? preId);
        }
        // Standard-Wasserzeichen/-Logo aus dem Profil vorauswählen (wie Overlay) —
        // nur wenn keine Session-Wiederherstellung die Auswahl bereits gesetzt hat.
        if (!sessionRestoredRef.current && profile) {
          if (profile.defaultWatermarkAssetId && wms.some((w) => w.id === profile.defaultWatermarkAssetId)) {
            setSelectedWatermarkId((prev) => prev ?? profile.defaultWatermarkAssetId);
            setWatermarkOpacity(profile.defaultWatermarkOpacity);
            setWatermarkScale(profile.defaultWatermarkScale);
            setWatermarkPosition(profile.defaultWatermarkPosition);
          }
          if (profile.defaultLogoAssetId && logos.some((l) => l.id === profile.defaultLogoAssetId)) {
            setSelectedLogoId((prev) => prev ?? profile.defaultLogoAssetId);
            setLogoPosition(profile.defaultLogoPosition);
          }
        }
      })
      .catch((err) => {
        console.error('[Wizard] Branding assets could not be loaded:', err);
        setOverlayAssets([]);
        setLogoAssets([]);
        setWatermarkAssets([]);
      });
  }, [step, overlayAssets, user?.id]);

  // Auswahl-Liste: pro Overlay nur eine Variante (4:3 bevorzugt) — die 1:1-
  // Geschwister werden nicht separat gezeigt. `overlayAssets` (voll) bleibt für
  // die Aspekt-Auflösung beim Export.
  const displayOverlays = useMemo(
    () => (overlayAssets ? overlaysForSelection(overlayAssets) : []),
    [overlayAssets]
  );
  // Aktiv gewähltes Overlay (Default: Standard bzw. erste Auswahl-Variante).
  const selectedOverlay =
    (overlayAssets && overlayAssets.find((o) => o.id === selectedOverlayId)) ||
    (displayOverlays.length > 0 ? displayOverlays[0] : null);
  const selectedLogo = logoAssets?.find((l) => l.id === selectedLogoId) ?? null;
  const selectedWatermark = watermarkAssets?.find((w) => w.id === selectedWatermarkId) ?? null;

  // Verarbeitetes Wasserzeichen (Weiß → transparent) für Live-Vorschau — Muster aus BrandingView.
  useEffect(() => {
    if (!selectedWatermark) {
      setWatermarkPreviewUrl(null);
      return;
    }
    let cancelled = false;
    const options: WatermarkOptions = { opacity: watermarkOpacity, position: watermarkPosition, scale: watermarkScale };
    processWatermark(selectedWatermark.publicUrl, 200, 200, options)
      .then((canvas) => {
        if (!cancelled) setWatermarkPreviewUrl(canvas.toDataURL());
      })
      .catch((err) => {
        console.error('[Wizard] Watermark preview failed:', err);
        if (!cancelled) setWatermarkPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedWatermark, watermarkOpacity, watermarkScale, watermarkPosition]);

  const handleAssetUpload = useCallback(
    async (file: File, kind: 'logo' | 'overlay' | 'watermark') => {
      if (!user?.id) return;
      if (kind === 'overlay' && file.type !== 'image/png') {
        setNotice('Bitte eine PNG-Datei mit Transparenz hochladen.');
        return;
      }
      setUploadingAssetKind(kind);
      setNotice(null);
      try {
        // Overlays: Seitenverhältnis automatisch erkennen (4:3/1:1) und taggen.
        const asset = kind === 'overlay'
          ? await uploadOverlayAsset(user.id, file)
          : await uploadBrandingAsset(user.id, file, kind);
        if (kind === 'overlay') {
          // Hochgeladenes Overlay direkt als Standard-Overlay speichern.
          await updateDefaultBrandingDefaults(user.id, { overlayAssetId: asset.id });
          setOverlayAssets((prev) => [asset, ...(prev ?? [])]);
          setDefaultOverlayId(asset.id);
          setSelectedOverlayId(asset.id);
          setOverlayChoice('own');
        } else if (kind === 'logo') {
          setLogoAssets((prev) => [asset, ...(prev ?? [])]);
          setSelectedLogoId(asset.id);
        } else {
          // Hochgeladenes Wasserzeichen direkt als Standard speichern.
          await updateDefaultBrandingDefaults(user.id, { watermarkAssetId: asset.id });
          setWatermarkAssets((prev) => [asset, ...(prev ?? [])]);
          setSelectedWatermarkId(asset.id);
        }
      } catch (err) {
        console.error('[Wizard] Asset upload failed:', err);
        setNotice('Upload fehlgeschlagen — bitte erneut versuchen.');
      } finally {
        setUploadingAssetKind(null);
      }
    },
    [user?.id]
  );

  /**
   * Zum Overlay-Builder springen und robust zu genau diesem Schritt zurückkehren:
   * Das Rücksprung-Ziel ist die Wizard-Route selbst (/app/wizard?p=…). Nach dem
   * Erstellen lädt App neu, rehydriert den Wizard im 'add'-Modus und stellt aus
   * der sessionStorage-Session (inkl. aktuellem Schritt = Branding & Download)
   * plus DB genau diesen Stand wieder her. Das neue Overlay ist dann als
   * Standard gesetzt und wird beim Neuladen der Assets vorausgewählt.
   */
  const handleOpenOverlayBuilder = useCallback(() => {
    const returnTarget = project ? `/app/wizard?p=${project.id}` : '/app/dashboard';
    window.location.assign('/app/overlay-builder?return=' + encodeURIComponent(returnTarget));
  }, [project]);

  const deliverableRooms = useMemo(() => rooms.filter((r) => r.emptyPath || r.stagedPath), [rooms]);
  const downloadFileCount = useMemo(
    () => deliverableRooms.reduce((n, r) => n + (r.emptyPath ? 1 : 0) + (r.stagedPath ? 1 : 0), 0),
    [deliverableRooms]
  );

  const brandingOptions: BrandingOptions = useMemo(
    () => ({
      logoUrl: selectedLogo?.publicUrl ?? null,
      logoPosition,
      // Passende Aspekt-Variante des Overlays zum Projekt-Format (Default 4:3).
      overlayUrl:
        overlayChoice === 'own' && selectedOverlay
          ? resolveOverlayUrlForAspect(selectedOverlay, overlayAssets ?? [], aspect)
          : null,
      watermarkUrl: selectedWatermark?.publicUrl ?? null,
      watermarkOpacity,
      watermarkPosition,
      watermarkScale,
    }),
    [selectedLogo, logoPosition, overlayChoice, selectedOverlay, overlayAssets, aspect, selectedWatermark, watermarkOpacity, watermarkPosition, watermarkScale]
  );
  const hasBrandingLayer = !!(brandingOptions.logoUrl || brandingOptions.overlayUrl || brandingOptions.watermarkUrl);

  // Branding geändert → gebrandete Varianten müssen neu persistiert werden.
  useEffect(() => {
    brandedPersistedRef.current = false;
  }, [brandingOptions]);

  /**
   * Brennt Logo/Overlay/Wasserzeichen in alle Deliverables und persistiert sie
   * als gebrandete Varianten am Bild (storage_path_branded/_branded_empty) —
   * damit sie nach dem Workflow im Projekt (Bilder-Tab + Popup) verfügbar sind.
   */
  const persistBrandedImages = useCallback(async (): Promise<boolean> => {
    const proj = project;
    if (!proj || !hasBrandingLayer || brandedPersistedRef.current) return true;
    if (savingBrandingRef.current) return true; // läuft bereits — kein Doppel-Persist
    savingBrandingRef.current = true;
    setIsSavingBranding(true);
    try {
      const payload: Array<{ base64: string; roomType: string; originalId: string; variant: 'staged' | 'empty' }> = [];
      let layerFailures = 0;
      for (const room of deliverableRooms) {
        if (!room.dbId) continue;
        const entries: Array<{ variant: 'staged' | 'empty'; path: string }> = [];
        if (room.emptyPath) entries.push({ variant: 'empty', path: room.emptyPath });
        if (room.stagedPath) entries.push({ variant: 'staged', path: room.stagedPath });
        for (const entry of entries) {
          const result = await applyBrandingDetailed(getPublicUrl(entry.path), brandingOptions);
          // Nichts als „gebrandet" speichern, wenn ein konfigurierter Layer
          // (z. B. das Wasserzeichen) nicht eingebrannt werden konnte.
          if (result.failedLayers.length > 0 || !result.url.startsWith('data:')) {
            layerFailures++;
            continue;
          }
          payload.push({ base64: result.url, roomType: roomDisplayName(room), originalId: room.dbId, variant: entry.variant });
        }
      }
      if (layerFailures > 0) {
        setNotice(
          'Das Branding (z. B. Wasserzeichen) konnte nicht auf alle Bilder angewendet werden — diese wurden nicht als gebrandete Version gespeichert. Tipp: dunkles Logo auf weißem oder transparentem Hintergrund verwenden.'
        );
      }
      if (payload.length > 0) {
        const { failed } = await uploadBrandedImagesToSupabase(proj.id, payload);
        if (failed > 0) {
          setNotice(`${failed} gebrandete ${failed === 1 ? 'Bild' : 'Bilder'} konnten nicht gespeichert werden.`);
          return false;
        }
      }
      if (layerFailures > 0) return false; // Retry möglich — nicht als persistiert markieren
      brandedPersistedRef.current = true;
      return true;
    } catch (err) {
      console.error('[Wizard] Branded persist failed:', err);
      setNotice('Gebrandete Bilder konnten nicht gespeichert werden — bitte erneut versuchen.');
      return false;
    } finally {
      savingBrandingRef.current = false;
      setIsSavingBranding(false);
    }
  }, [project, hasBrandingLayer, deliverableRooms, brandingOptions]);

  const handleDownload = useCallback(async () => {
    const proj = project;
    if (!proj || isDownloading || downloadFileCount === 0) return;
    // Warnen, wenn das gewählte Overlay keine Variante fürs Projekt-Format hat.
    if (
      overlayChoice === 'own' &&
      overlayNeedsAspectWarning(selectedOverlay, overlayAssets ?? [], ASPECT_VALUE[aspect]) &&
      !window.confirm(
        `Das gewählte Overlay hat keine ${aspect}-Variante. Es wird verzerrt auf die Bilder gelegt. Trotzdem herunterladen?`
      )
    ) {
      return;
    }
    setIsDownloading(true);
    setNotice(null);
    try {
      const ext = exportCompressed ? 'jpg' : 'png';

      // Gleiches flaches Namensschema wie ProjectDetails/Workspace-Export
      // (Immobilienbilder/Kueche_01_leer.png) statt Unterordner pro Raum.
      const exportEntries: ExportEntry[] = [];
      for (const room of deliverableRooms) {
        const imageId = room.dbId ?? room.localId;
        const roomLabel = roomDisplayName(room);
        if (room.emptyPath) exportEntries.push({ imageId, roomLabel, variant: 'leer', source: getPublicUrl(room.emptyPath), ext });
        if (room.stagedPath) exportEntries.push({ imageId, roomLabel, variant: 'eingerichtet', source: getPublicUrl(room.stagedPath), ext });
      }

      // Ohne Branding/Kompression/KI-Hinweis unverändert exportieren
      // (Original-Bytes, kein Re-Encoding) — sonst Branding einbrennen,
      // optional komprimieren und zuletzt den Hinweis daraufsetzen.
      // Der Assistent brandet erst beim Packen, deshalb hängt der Hinweis hier
      // an der Transform-Kette statt an einem Pre-Step über den Entries.
      const needsRender = hasBrandingLayer || exportCompressed;
      const result = await assembleExportZip(planExportFiles(exportEntries, []), {
        transform:
          needsRender || aiLabel.enabled
            ? async (source) => {
                const rendered = needsRender
                  ? await renderBrandedExport(source as string, brandingOptions, exportCompressed ? { aspectRatio: aspect } : undefined)
                  : source;
                return aiLabel.enabled ? applyAiLabelToImage(rendered, aiLabel.position, aiLabel.opacity) : rendered;
              }
            : undefined,
      });
      saveAs(result.blob, exportZipFileName(proj.name));
      if (result.failed > 0) {
        setNotice(`${result.added} von ${result.total} Dateien exportiert — einige Bilder konnten nicht geladen werden.`);
      }
      // Zuletzt genutztes Branding als Standard fürs nächste Projekt merken.
      if (user?.id) {
        void updateDefaultBrandingDefaults(user.id, {
          watermarkAssetId: selectedWatermarkId,
          watermarkOpacity,
          watermarkScale: Math.round(watermarkScale * 100),
          watermarkPosition,
          logoAssetId: selectedLogoId,
          logoPosition,
        });
      }
      // Gebrandete Varianten ins Projekt persistieren — Fehler blockieren den
      // Exit nicht (Notice reicht, das ZIP ist bereits gespeichert).
      await persistBrandedImages();
      // Flow abgeschlossen → Session verwerfen (nächster Aufruf startet frisch)
      clearWizardSession(proj.id);
      onExit('workspace', proj.id);
    } catch (err) {
      console.error('[Wizard] ZIP export failed:', err);
      // Abbrechen statt still ohne Wasserzeichen/Logo exportieren.
      setNotice(
        err instanceof BrandingLayerError
          ? 'Das Branding (z. B. Wasserzeichen) konnte nicht angewendet werden — Download abgebrochen. Tipp: dunkles Logo auf weißem oder transparentem Hintergrund verwenden, oder die Auswahl entfernen.'
          : 'Fehler beim Erstellen der ZIP-Datei. Bitte erneut versuchen.'
      );
    } finally {
      setIsDownloading(false);
    }
  }, [project, isDownloading, downloadFileCount, exportCompressed, deliverableRooms, aspect, onExit, brandingOptions, hasBrandingLayer, persistBrandedImages, overlayChoice, selectedOverlay, overlayAssets, user?.id, selectedWatermarkId, watermarkOpacity, watermarkScale, watermarkPosition, selectedLogoId, logoPosition, aiLabel.enabled, aiLabel.position, aiLabel.opacity]);

  // ==========================================================================
  // Abgeleitete Werte
  // ==========================================================================

  const emptyCost = rooms.length;
  const hasEnoughForEmpty = credits >= emptyCost;

  const emptyTargets = rooms.filter((r) => r.storagePath || r.emptyStatus !== 'idle');
  const emptyDoneCount = rooms.filter((r) => r.emptyStatus === 'done').length;
  const emptyErrorCount = rooms.filter((r) => r.emptyStatus === 'error').length;
  const isEmptying = rooms.some((r) => r.emptyStatus === 'running');
  const allEmptySettled = rooms.length > 0 && rooms.every((r) => r.emptyStatus === 'done' || r.emptyStatus === 'error');

  const stagedQueued = rooms.filter((r) => r.stagedStatus === 'queued').length;
  const stagedRunning = rooms.filter((r) => r.stagedStatus === 'running').length;
  const isStaging = stagedQueued > 0 || stagedRunning > 0;

  // Laufen gerade Generierungen im Flow (Leeren oder Einrichten)?
  const flowBusy = isEmptying || isStaging;

  // Reload / Tab schließen abfangen …
  useUnloadWarning(flowBusy);
  // … und die App informieren (sperrt/​warnt bei Sidebar-Navigation).
  useEffect(() => {
    onBusyChange?.(flowBusy);
  }, [flowBusy, onBusyChange]);
  // Beim Unmount sicher zurücksetzen, damit kein „busy" hängen bleibt.
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);

  const hasAnyDbImages = rooms.some((r) => r.dbId);

  const handleSaveAndClose = useCallback(async () => {
    if (
      flowBusy &&
      !window.confirm('Es läuft gerade eine Generierung. Wenn du jetzt schließt, siehst du das Ergebnis erst später im Projekt. Trotzdem schließen?')
    ) {
      return;
    }
    // Auf Schritt 4 mit aktiven Branding-Layern: gebrandete Varianten vor dem
    // Exit persistieren (Fehler zeigen eine Notice, blockieren aber nicht).
    if (step === 4) {
      await persistBrandedImages();
    }
    if (project && hasAnyDbImages) {
      onExit('workspace', project.id);
    } else {
      onExit('dashboard');
    }
  }, [project, hasAnyDbImages, onExit, flowBusy, step, persistBrandedImages]);

  // ==========================================================================
  // Render-Bausteine
  // ==========================================================================

  // Schritt-Leiste im Stil der Design-Vorlage: große Kreise, Label darunter,
  // Verbindungslinien färben sich mit dem Fortschritt.
  const renderStepIndicator = () => (
    <div className="mx-auto flex w-full max-w-3xl items-start">
      {([1, 2, 3, 4] as WizardStep[]).map((s, i) => {
        const isDone = s < step;
        const isCurrent = s === step;
        return (
          <Fragment key={s}>
            {i > 0 && (
              <div
                className={cn(
                  'mt-[23px] h-0.5 min-w-4 flex-1 rounded-full sm:mx-2',
                  isDone || isCurrent ? 'bg-primary' : 'bg-border'
                )}
              />
            )}
            <div className="flex w-16 flex-col items-center gap-2 sm:w-24">
              <div
                className={cn(
                  'flex h-12 w-12 items-center justify-center rounded-full text-base font-bold transition-shadow',
                  isDone && 'bg-primary text-primary-foreground',
                  isCurrent && 'bg-primary text-primary-foreground ring-[6px] ring-primary/15',
                  !isDone && !isCurrent && 'border-2 border-border bg-card text-muted-foreground'
                )}
              >
                {isDone ? <Check className="h-5 w-5" /> : s}
              </div>
              <span
                className={cn(
                  'text-center text-xs font-semibold leading-tight sm:text-sm',
                  isDone || isCurrent ? 'text-foreground' : 'text-muted-foreground',
                  isCurrent && 'font-bold'
                )}
              >
                {STEP_LABELS[s]}
              </span>
            </div>
          </Fragment>
        );
      })}
    </div>
  );

  const renderCreditBadge = () => (
    <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
      <Coins className="h-4 w-4 text-amber-500" />
      <span className="text-sm font-medium text-foreground">{formatCredits(credits)} Credits</span>
    </div>
  );

  const renderNotice = () =>
    notice && (
      <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <span>{notice}</span>
      </div>
    );

  // --------------------------------------------------------------------------
  // Schritt 1 — Hochladen
  // --------------------------------------------------------------------------

  const renderStep1 = () => {
    // Pflichtfelder werden hervorgehoben, bis sie ausgefüllt sind.
    // Name-Feld nur zeigen, wenn das Projekt noch nicht existiert (wird sonst
    // bereits im „Neues Projekt"-Modal gesetzt).
    const showNameField = mode === 'new' && !project;
    const nameMissing = showNameField && !projectName.trim();
    const photosMissing = rooms.length === 0;

    return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-foreground">Fotos hochladen</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Lade die Originalfotos der Immobilie hoch — Raumtypen und Format legst du im nächsten Schritt fest.
        </p>
      </div>

      <div className={cn('grid gap-4', showNameField && 'md:grid-cols-2')}>
        {showNameField && (
          <div
            className={cn(
              'rounded-xl border bg-card p-4 transition-colors',
              nameMissing ? 'border-primary/60 bg-primary/5' : 'border-border'
            )}
          >
            <Label htmlFor="wizard-project-name" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Projektname <span className="text-primary">*</span>
            </Label>
            <Input
              id="wizard-project-name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="z. B. Waldstraße 8"
              disabled={!!project}
              className={cn(
                'mt-2',
                nameMissing && 'border-primary ring-1 ring-primary/30 focus-visible:ring-primary'
              )}
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              Pflichtfeld — z. B. Straße und Hausnummer, damit du das Projekt später wiederfindest.
            </p>
          </div>
        )}

        <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-4">
          <Link2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">Keine Fotos zur Hand?</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Sende Mieter oder Eigentümer einen Upload-Link — die Fotos landen automatisch in diesem Projekt.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyTenantLink}
              disabled={linkState === 'busy'}
              className="mt-2 gap-2"
            >
              {linkState === 'busy' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              {linkState === 'copied'
                ? 'Mieter-Link kopiert ✓'
                : linkState === 'error'
                  ? 'Link konnte nicht kopiert werden'
                  : 'Link an Mieter/Eigentümer senden'}
            </Button>
          </div>
        </div>
      </div>

      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <label
          className={cn(
            'block cursor-pointer rounded-2xl border-2 border-dashed py-16 text-center transition-colors',
            isDragging
              ? 'border-primary bg-primary/10 text-primary'
              : photosMissing
                ? 'border-primary bg-primary/5 text-foreground hover:bg-primary/10'
                : 'border-muted bg-muted/30 text-muted-foreground hover:border-primary/50 hover:bg-primary/5'
          )}
        >
          <CloudUpload className={cn('mx-auto mb-3 h-12 w-12', isDragging || photosMissing ? 'text-primary' : 'opacity-50')} />
          <p className="text-base font-semibold">
            {isDragging ? 'Bilder hier ablegen' : 'Fotos hierher ziehen oder klicken'}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">JPG, PNG oder HEIC · bis {MAX_PHOTOS} Bilder</p>
          {!isDragging && (
            <span className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm">
              <Camera className="h-4 w-4" /> Fotos auswählen
            </span>
          )}
          <input
            type="file"
            multiple
            className="hidden"
            accept="image/*,.heic,.heif"
            onChange={(e) => {
              handleFilesSelected(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
          />
        </label>
      </div>

      <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2">
          <Check className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
          iPhone-Fotos (HEIC) werden automatisch konvertiert
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2">
          <Check className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
          Weitwinkel &amp; gutes Licht liefern die besten Ergebnisse
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2">
          <Check className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
          Leeren 1 Credit/Raum · Nachbessern 0,5 Credits
        </div>
      </div>

      {rooms.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {rooms.length} Foto{rooms.length === 1 ? '' : 's'} ausgewählt — Raumtypen legst du im nächsten Schritt fest.
          </p>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
            {rooms.map((room) => (
              <div key={room.localId} className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-border bg-muted">
                <HeicSafeImage src={room.previewUrl} alt="Foto" className="h-full w-full object-cover" />
                {room.isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <Loader2 className="h-5 w-5 animate-spin text-white" />
                  </div>
                )}
                {room.uploadError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-destructive/80 p-1" title={room.uploadError}>
                    <AlertCircle className="h-5 w-5 text-white" />
                  </div>
                )}
                {room.dbId && !room.isUploading && (
                  <Badge className="absolute left-1 top-1 gap-0.5 bg-emerald-500 px-1.5 py-0 text-[10px] hover:bg-emerald-600">
                    <Check className="h-2.5 w-2.5" />
                  </Badge>
                )}
                <button
                  type="button"
                  onClick={() => void handleRemoveRoom(room)}
                  className="absolute right-1 top-1 rounded-md bg-black/50 p-1 text-white/80 opacity-0 transition-opacity hover:bg-destructive hover:text-white group-hover:opacity-100"
                  title="Foto entfernen"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {renderNotice()}

      <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
        <Button
          onClick={() => void handleStep1Next()}
          disabled={rooms.length === 0 || (mode === 'new' && !project && !projectName.trim()) || isAdvancing}
          className="gap-2"
        >
          {isAdvancing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Fotos werden hochgeladen…
            </>
          ) : (
            <>
              Weiter <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
    );
  };

  // --------------------------------------------------------------------------
  // Schritt 2 — Einstellen
  // --------------------------------------------------------------------------

  const renderRoomTypeSelect = (room: WizardRoom) => (
    <Select value={room.roomType} onValueChange={(v) => handleRoomTypeChange(room, v as RoomType)}>
      {/* Dezente Primär-Hervorhebung: Nutzer sollen die Raumtypen aktiv prüfen/korrigieren */}
      <SelectTrigger className="w-full border-primary/40 bg-primary/5">
        <SelectValue placeholder="Raumtyp wählen" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>Innenbereich</SelectLabel>
          {getIndoorRooms()
            .filter((r) => r.id !== 'custom')
            .map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {r.label}
              </SelectItem>
            ))}
        </SelectGroup>
        <SelectGroup>
          <SelectLabel>Außenbereich</SelectLabel>
          {getOutdoorRooms().map((r) => (
            <SelectItem key={r.id} value={r.id}>
              {r.label}
            </SelectItem>
          ))}
        </SelectGroup>
        <SelectGroup>
          <SelectLabel>Sonstiges</SelectLabel>
          <SelectItem value="custom">Sonstiges</SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      {/* Projektweites Format */}
      <div className="max-w-md rounded-xl border border-border bg-muted/30 p-4">
        <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Format der Ausgabebilder</Label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {(
            [
              { value: '4:3' as AspectRatio, label: 'Querformat', icon: RectangleHorizontal, hint: '4:3 — Klassisch' },
              { value: '1:1' as AspectRatio, label: 'Quadrat', icon: Square, hint: '1:1 — Social Media' },
            ]
          ).map(({ value, label, icon: Icon, hint }) => {
            const isSelected = aspect === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => handleAspectChange(value)}
                className={cn(
                  'flex flex-col items-center gap-1.5 rounded-lg border-2 p-3 transition-all',
                  isSelected ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/30'
                )}
              >
                <Icon className={cn('h-5 w-5', isSelected ? 'text-primary' : 'text-muted-foreground')} />
                <span className={cn('text-sm font-medium', isSelected ? 'text-primary' : 'text-foreground')}>{label}</span>
                <span className="text-[10px] leading-tight text-muted-foreground">{hint}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Pro-Foto-Karten */}
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 lg:grid-cols-3">
        {rooms.map((room) => (
          <Card key={room.localId} className="overflow-hidden">
            <div className="relative aspect-[4/3] bg-muted">
              <HeicSafeImage src={room.previewUrl} alt={roomDisplayName(room)} className="h-full w-full object-cover" />
            </div>
            <CardContent className="space-y-3 p-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Raumtyp</span>
                <div className="mt-1.5">{renderRoomTypeSelect(room)}</div>
              </div>
              {room.roomType === RoomType.CUSTOM && (
                <Input
                  value={room.customLabel ?? ''}
                  onChange={(e) => handleCustomLabelChange(room, e.target.value)}
                  placeholder="Eigene Bezeichnung, z. B. Hobbyraum"
                />
              )}
              {room.roomType === RoomType.KITCHEN && (
                <div className="flex items-center space-x-2 rounded-lg border bg-muted/20 p-2">
                  <Switch
                    id={`keep-kitchen-${room.localId}`}
                    checked={room.keepKitchen}
                    onCheckedChange={(checked) => handleKeepKitchenChange(room, checked)}
                  />
                  <div className="flex flex-col">
                    <Label htmlFor={`keep-kitchen-${room.localId}`} className="cursor-pointer text-sm font-medium">
                      Küche behalten?
                    </Label>
                    <span className="text-[10px] text-muted-foreground">Einbauküche bleibt beim Leerräumen erhalten.</span>
                  </div>
                </div>
              )}
              {!supportsStaging(room.roomType) && (
                <Badge variant="secondary" className="text-[10px]">
                  Wird geleert, aber nicht eingerichtet
                </Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <p className="text-center text-xs text-muted-foreground">
        {emptyCost} {emptyCost === 1 ? 'Raum wird' : 'Räume werden'} leergeräumt ({emptyCost} {emptyCost === 1 ? 'Credit' : 'Credits'}).
        Einrichten wählst du danach.
      </p>

      {!hasEnoughForEmpty && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Nicht genug Credits: Zum Leerräumen werden {emptyCost} Credits benötigt, verfügbar sind {formatCredits(credits)}.
            Lade dein Guthaben unter „Abo &amp; Guthaben" auf.
          </span>
        </div>
      )}

      {renderNotice()}

      <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          {renderCreditBadge()}
          <span className={cn('text-sm', hasEnoughForEmpty ? 'text-muted-foreground' : 'text-destructive')}>
            Kosten Leeren: {emptyCost} {emptyCost === 1 ? 'Credit' : 'Credits'}
          </span>
        </div>
        <div className="flex flex-1 justify-end gap-3">
          <Button variant="ghost" onClick={() => setStep(1)} className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Zurück
          </Button>
          <Button onClick={() => void handleStartEmptying()} disabled={!hasEnoughForEmpty || rooms.length === 0} className="gap-2">
            <Wand2 className="h-4 w-4" />
            Weiter: Leeren ({emptyCost} {emptyCost === 1 ? 'Credit' : 'Credits'})
          </Button>
        </div>
      </div>
    </div>
  );

  // --------------------------------------------------------------------------
  // Schritt 3 — Prüfen & Einrichten (Leer-Kontrolle + Staging pro Raum)
  // --------------------------------------------------------------------------

  /** Klickbares Ergebnis-Preview (rechte Seite) mit Prüfen-Chip und Ladebalken. */
  const renderResultPreview = (room: WizardRoom, kind: 'empty' | 'staged') => {
    const status = kind === 'empty' ? room.emptyStatus : room.stagedStatus;
    const url = kind === 'empty' ? room.emptyUrl : room.stagedUrl;
    const error = kind === 'empty' ? room.emptyError : room.stagedError;
    const label = kind === 'empty' ? 'Leergeräumt' : 'Eingerichtet';
    return (
      <button
        type="button"
        onClick={() => status === 'done' && openInspectorFor(room)}
        disabled={status !== 'done'}
        className={cn(
          'group relative block aspect-[4/3] w-full overflow-hidden rounded-lg border-2 bg-muted text-left transition-all',
          status === 'done'
            ? 'cursor-pointer border-primary/40 hover:scale-[1.02] hover:border-primary hover:shadow-lg hover:ring-2 hover:ring-primary/30'
            : 'border-border'
        )}
      >
        {status === 'done' && url ? (
          <img src={url} alt={`${roomDisplayName(room)} — ${label}`} className="h-full w-full object-cover" />
        ) : kind === 'staged' && room.emptyUrl ? (
          <img src={room.emptyUrl} alt={roomDisplayName(room)} className="h-full w-full object-cover opacity-30" />
        ) : (
          <HeicSafeImage src={room.previewUrl} alt={roomDisplayName(room)} className="h-full w-full object-cover opacity-30" />
        )}
        {status === 'queued' && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/45 px-4">
            <Loader2 className="h-5 w-5 animate-spin text-white/80" />
            <span className="text-xs font-medium text-white">In Warteschlange…</span>
          </div>
        )}
        {status === 'running' && (
          <TimedProgress label={kind === 'empty' ? 'Wird leergeräumt… (~60 s)' : 'Wird eingerichtet… (~60 s)'} />
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-destructive/70 p-2">
            <AlertCircle className="h-5 w-5 text-white" />
            <span className="line-clamp-2 text-center text-[11px] text-white">{error}</span>
          </div>
        )}
        {status === 'done' && (
          <span className="absolute right-1.5 top-1.5 flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-[11px] font-medium text-white transition-colors group-hover:bg-primary">
            <Pencil className="h-3 w-3" /> Prüfen
          </span>
        )}
        <span className="absolute bottom-1.5 left-1.5 rounded-md bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white">
          {label}
        </span>
      </button>
    );
  };

  /**
   * A/B-Varianten-Strip direkt unter dem Leer-Ergebnis (genau eine aktiv).
   * Label oben statt seitlich gequetscht; ab 3 Varianten horizontal scrollbar
   * mit Gradient am rechten Rand als Überlauf-Hinweis.
   */
  const renderEmptyVariantPicker = (room: WizardRoom) => {
    const variants = room.emptyVariants ?? [];
    if (room.emptyStatus !== 'done' || variants.length < 2) return null;
    const hasOverflow = variants.length > 2;
    return (
      <div className="space-y-1.5">
        <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Leer-Variante wählen <span className="font-normal normal-case">· Basis fürs Einrichten</span>
        </span>
        <div className="relative">
          <div className={cn('flex gap-2 pb-1', hasOverflow && 'overflow-x-auto pr-8 [scrollbar-width:thin]')}>
            {variants.map((v, i) => {
              const variantLabel = String.fromCharCode(65 + i); // A, B, …
              const isActive = v.storagePath === room.emptyPath;
              return (
                <button
                  key={v.storagePath}
                  type="button"
                  onClick={() => void handleSelectEmptyVariant(room, v)}
                  title={`Leer-Variante ${variantLabel} verwenden`}
                  className={cn(
                    'relative aspect-[4/3] w-20 flex-shrink-0 overflow-hidden rounded-md border-2 transition-all',
                    isActive
                      ? 'border-primary ring-2 ring-primary/30'
                      : 'border-border opacity-70 hover:border-primary/50 hover:opacity-100'
                  )}
                >
                  <img src={v.imageUrl} alt={`Variante ${variantLabel}`} className="h-full w-full object-cover" />
                  <span
                    className={cn(
                      'absolute left-1 top-1 rounded bg-black/60 px-1 text-[10px] font-bold text-white',
                      isActive && 'bg-primary'
                    )}
                  >
                    {variantLabel}
                  </span>
                  {isActive && (
                    <span className="absolute bottom-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                      <Check className="h-2.5 w-2.5" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {hasOverflow && (
            <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-card to-transparent" />
          )}
        </div>
      </div>
    );
  };

  /** Dritte Spalte: Staging-Zustand des Raums (CTA · Fortschritt · Ergebnis). */
  const renderStagedCell = (room: WizardRoom) => {
    if (!supportsStaging(room.roomType)) {
      return (
        <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-border bg-muted/40 p-3 text-center">
          <Badge variant="secondary" className="text-[10px]">bleibt leer</Badge>
          <span className="text-[11px] leading-tight text-muted-foreground">
            Außenbereiche werden nicht eingerichtet.
          </span>
        </div>
      );
    }
    if (room.stagedStatus === 'idle') {
      const canStage = room.emptyStatus === 'done' && !!room.emptyPath;
      const hasCredit = credits >= 1;
      return (
        <button
          type="button"
          onClick={() => void handleStageRoom(room)}
          disabled={!canStage || !hasCredit}
          title={!hasCredit ? 'Nicht genug Credits' : undefined}
          className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary/40 bg-primary/5 p-3 text-center transition-colors hover:border-primary hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-primary/40 disabled:hover:bg-primary/5"
        >
          <Sparkles className="h-5 w-5 text-primary" />
          <span className="text-sm font-semibold text-foreground">Staging erstellen</span>
          <span className="text-[11px] text-muted-foreground">1 Credit · Möbliert auf dem leeren Bild</span>
        </button>
      );
    }
    return (
      <div className="space-y-2">
        {renderResultPreview(room, 'staged')}
        {room.stagedStatus === 'error' && (
          <Button size="sm" variant="outline" className="w-full gap-1.5" onClick={() => void handleStageRoom(room)}>
            <RefreshCw className="h-3.5 w-3.5" /> Erneut versuchen
          </Button>
        )}
      </div>
    );
  };

  /** Karte pro Raum: Original · Leer (A/B-Wahl) · Eingerichtet — v1-Muster. */
  const renderReviewCard = (room: WizardRoom) => (
    <div key={room.localId} className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-sm font-bold text-foreground">{roomDisplayName(room)}</span>
        <span className="flex items-center gap-1.5">
          {room.emptyStatus === 'done' && (
            <Badge variant="secondary" className="gap-1 text-[10px]">
              <Check className="h-2.5 w-2.5" /> geleert
            </Badge>
          )}
          {room.stagedStatus === 'done' && (
            <Badge variant="secondary" className="gap-1 text-[10px]">
              <Check className="h-2.5 w-2.5" /> eingerichtet
            </Badge>
          )}
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="relative aspect-[4/3] overflow-hidden rounded-lg border border-border bg-muted">
          <HeicSafeImage src={room.previewUrl} alt={roomDisplayName(room)} className="h-full w-full object-cover" />
          <span className="absolute bottom-1.5 left-1.5 rounded-md bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white">
            Original
          </span>
        </div>
        <div className="space-y-2">
          {renderResultPreview(room, 'empty')}
          {room.emptyStatus === 'error' && (
            <Button size="sm" variant="outline" className="w-full gap-1.5" onClick={() => void runEmptyForRoom(room)}>
              <RefreshCw className="h-3.5 w-3.5" /> Erneut versuchen
            </Button>
          )}
          {renderEmptyVariantPicker(room)}
        </div>
        <div>{renderStagedCell(room)}</div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-foreground">
            {allEmptySettled ? 'Prüfen & Einrichten' : 'Räume werden leergeräumt…'}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {allEmptySettled
              ? 'Prüfe die Leer-Ergebnisse und richte Räume direkt in der Karte ein (1 Credit/Raum).'
              : 'Jeder Raum wird zuerst sauber geleert — das gibt beim Einrichten das beste Ergebnis.'}
          </p>
          {!allEmptySettled && (
            <p className="mt-1 text-xs text-muted-foreground">
              {emptyDoneCount + emptyErrorCount} von {emptyTargets.length} fertig{isEmptying ? ' · läuft…' : ''}
            </p>
          )}
        </div>
        {/* Stil global — gilt für alle Räume dieses Durchlaufs */}
        <div className="w-full sm:w-60">
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Einrichtungsstil (alle Räume)
          </Label>
          <div className="mt-1.5">
            <Select value={style} onValueChange={(v) => handleStyleChange(v as StagingStyle)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Stil wählen" />
              </SelectTrigger>
              <SelectContent>
                {STAGING_STYLES.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.label} — {s.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Hinweis: Ergebnisse aktiv prüfen */}
      <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-foreground">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
        <span>
          <span className="font-semibold">Prüfe deine Bilder auf Richtigkeit.</span> Klicke auf ein Ergebnis, um es
          groß zu prüfen und nachzubessern (0,5 Credits). Unter dem Leer-Bild wählst du die Variante (A/B) — die
          aktive Variante ist die Basis fürs Einrichten.
        </span>
      </div>

      {emptyErrorCount > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            {emptyErrorCount} {emptyErrorCount === 1 ? 'Raum konnte' : 'Räume konnten'} nicht geleert werden — nutze
            „Erneut versuchen" an der jeweiligen Karte.
          </span>
        </div>
      )}

      <div className="grid gap-4">{rooms.map(renderReviewCard)}</div>

      {renderNotice()}

      <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center">
        {renderCreditBadge()}
        <div className="flex flex-1 justify-between gap-3 sm:justify-end">
          <Button variant="ghost" onClick={() => setStep(2)} className="gap-2" disabled={isEmptying}>
            <ArrowLeft className="h-4 w-4" /> Einstellungen
          </Button>
          <Button
            onClick={() => setStep(4)}
            disabled={isEmptying || isStaging || emptyDoneCount === 0}
            className="gap-2"
          >
            Weiter: Branding &amp; Download <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  // --------------------------------------------------------------------------
  // Schritt 4 — Branding & Download
  // --------------------------------------------------------------------------

  /** Bibliothek + Hochladen für eine Asset-Art (Logo/Overlay/Wasserzeichen). */
  const renderAssetPicker = (
    kind: 'logo' | 'overlay' | 'watermark',
    assets: BrandingAsset[] | null,
    selectedId: string | null,
    onSelect: (id: string | null) => void,
    accept: string,
    defaultId?: string | null
  ) => (
    <div className="space-y-2">
      {assets === null ? (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Wird geladen…
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {assets.map((asset) => {
            const active = asset.id === selectedId;
            const isDefault = defaultId != null && asset.id === defaultId;
            return (
              <button
                key={asset.id}
                type="button"
                onClick={() => onSelect(active ? null : asset.id)}
                title={active ? `${asset.name} — Klick entfernt die Auswahl` : asset.name}
                className={cn(
                  'relative aspect-[4/3] overflow-hidden rounded-lg border-2 bg-muted/30 transition-all',
                  active ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/40'
                )}
              >
                <img src={asset.publicUrl} alt={asset.name} className="h-full w-full object-contain p-1" />
                {active && (
                  <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                    <Check className="h-2.5 w-2.5" />
                  </span>
                )}
                {isDefault && (
                  <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-white">
                    Standard
                  </span>
                )}
              </button>
            );
          })}
          <label
            className={cn(
              'flex aspect-[4/3] cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary',
              uploadingAssetKind === kind && 'pointer-events-none opacity-60'
            )}
          >
            {uploadingAssetKind === kind ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            <span className="text-[10px] font-medium">Hochladen</span>
            <input
              type="file"
              className="hidden"
              accept={accept}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void handleAssetUpload(file, kind);
              }}
            />
          </label>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => {
    const stagedCount = deliverableRooms.filter((r) => r.stagedPath).length;
    const previewRoom = deliverableRooms[0] ?? null;
    const previewBase = previewRoom ? previewRoom.stagedUrl ?? previewRoom.emptyUrl ?? null : null;

    const logoPositionButtons: { position: LogoPosition; icon: React.ReactNode; label: string }[] = [
      { position: 'top-left', icon: <AlignStartVertical className="h-4 w-4" />, label: 'OL' },
      { position: 'top-right', icon: <AlignEndVertical className="h-4 w-4" />, label: 'OR' },
      { position: 'bottom-left', icon: <AlignStartHorizontal className="h-4 w-4" />, label: 'UL' },
      { position: 'bottom-right', icon: <AlignEndHorizontal className="h-4 w-4" />, label: 'UR' },
    ];
    const watermarkPositionButtons: { position: WatermarkPosition; icon: React.ReactNode; label: string }[] = [
      { position: 'center', icon: <Square className="h-4 w-4" />, label: 'Mitte' },
      { position: 'tile', icon: <Grid3X3 className="h-4 w-4" />, label: 'Gekachelt' },
      { position: 'tl', icon: <CornerLeftUp className="h-4 w-4" />, label: 'OL' },
      { position: 'tr', icon: <CornerRightUp className="h-4 w-4" />, label: 'OR' },
      { position: 'bl', icon: <CornerLeftDown className="h-4 w-4" />, label: 'UL' },
      { position: 'br', icon: <CornerRightDown className="h-4 w-4" />, label: 'UR' },
    ];

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground">Branding &amp; Download</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Logo, Overlay und Wasserzeichen werden beim Download in die Bilder eingebrannt — der Download ist
              kostenlos.
            </p>
          </div>
          <span className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm font-semibold text-foreground">
            {deliverableRooms.length} {deliverableRooms.length === 1 ? 'Bild' : 'Bilder'} · davon {stagedCount} eingerichtet
          </span>
        </div>

        <div className="grid items-start gap-4 lg:grid-cols-[minmax(320px,380px)_1fr]">
          {/* Linke Spalte: kompakte, einklappbare Sektionen — alles in der ersten Fold */}
          <Accordion type="single" collapsible defaultValue="logo" className="rounded-xl border border-border bg-card px-4">
            <AccordionItem value="logo">
              <AccordionTrigger className="text-sm font-semibold">
                <span className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-primary" /> Logo
                  {selectedLogo && <Badge variant="secondary" className="text-[10px]">aktiv</Badge>}
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                {renderAssetPicker('logo', logoAssets, selectedLogoId, setSelectedLogoId, 'image/png,image/jpeg')}
                {selectedLogo && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Position</Label>
                    <div className="mt-1.5 grid grid-cols-4 gap-1.5">
                      {logoPositionButtons.map(({ position, icon, label }) => (
                        <button
                          key={position}
                          type="button"
                          onClick={() => setLogoPosition(position)}
                          className={cn(
                            'flex flex-col items-center gap-0.5 rounded-lg border-2 py-1.5 text-[10px] transition-colors',
                            logoPosition === position
                              ? 'border-primary bg-primary/5 text-primary'
                              : 'border-border text-muted-foreground hover:border-primary/40'
                          )}
                        >
                          {icon}
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="overlay">
              <AccordionTrigger className="text-sm font-semibold">
                <span className="flex items-center gap-2">
                  <Square className="h-4 w-4 text-primary" /> Overlay
                  {overlayChoice === 'own' && selectedOverlay && (
                    <Badge variant="secondary" className="text-[10px]">aktiv</Badge>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                {overlayAssets === null ? (
                  <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Wird geladen…
                  </div>
                ) : displayOverlays.length === 0 ? (
                  /* Noch kein Overlay definiert → drei Optionen */
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setOverlayChoice('none')}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-lg border-2 p-2.5 text-left text-sm transition-all',
                        overlayChoice === 'none' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                      )}
                    >
                      <span
                        className={cn(
                          'h-4 w-4 flex-shrink-0 rounded-full border-2',
                          overlayChoice === 'none' ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                        )}
                      />
                      <span>
                        <span className="block font-medium text-foreground">Ohne Overlay herunterladen</span>
                        <span className="block text-[11px] text-muted-foreground">Bilder ohne Overlay exportieren.</span>
                      </span>
                    </button>

                    <label
                      className={cn(
                        'flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-2 border-border p-2.5 text-left text-sm transition-all hover:border-primary/40',
                        uploadingAssetKind === 'overlay' && 'pointer-events-none opacity-60'
                      )}
                    >
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        {uploadingAssetKind === 'overlay' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      </span>
                      <span>
                        <span className="block font-medium text-foreground">Ich habe bereits ein Overlay</span>
                        <span className="block text-[11px] text-muted-foreground">
                          {uploadingAssetKind === 'overlay' ? 'Wird hochgeladen…' : 'PNG hochladen — wird als Standard gespeichert.'}
                        </span>
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/png"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (file) void handleAssetUpload(file, 'overlay');
                        }}
                      />
                    </label>

                    <button
                      type="button"
                      onClick={handleOpenOverlayBuilder}
                      className="flex w-full items-center gap-2.5 rounded-lg border-2 border-border p-2.5 text-left text-sm transition-all hover:border-primary/40"
                    >
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Sparkles className="h-4 w-4" />
                      </span>
                      <span>
                        <span className="block font-medium text-foreground">Neues Overlay erstellen</span>
                        <span className="block text-[11px] text-muted-foreground">
                          Im Overlay-Builder — danach geht es hier weiter.
                        </span>
                      </span>
                    </button>
                  </div>
                ) : (
                  /* Overlay(s) vorhanden → Ohne / Mit Overlay (Standard vorausgewählt) */
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setOverlayChoice('none')}
                        className={cn(
                          'flex items-center gap-2 rounded-lg border-2 p-2.5 text-left text-sm transition-all',
                          overlayChoice === 'none' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                        )}
                      >
                        <span
                          className={cn(
                            'h-4 w-4 flex-shrink-0 rounded-full border-2',
                            overlayChoice === 'none' ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                          )}
                        />
                        <span className="font-medium text-foreground">Ohne Overlay</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setOverlayChoice('own')}
                        className={cn(
                          'flex items-center gap-2 rounded-lg border-2 p-2.5 text-left text-sm transition-all',
                          overlayChoice === 'own' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                        )}
                      >
                        <span
                          className={cn(
                            'h-4 w-4 flex-shrink-0 rounded-full border-2',
                            overlayChoice === 'own' ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                          )}
                        />
                        <span className="font-medium text-foreground">Mit Overlay</span>
                      </button>
                    </div>

                    {overlayChoice === 'own' && (
                      <>
                        {renderAssetPicker(
                          'overlay',
                          displayOverlays,
                          selectedOverlay?.id ?? null,
                          (id) => {
                            if (id) {
                              setSelectedOverlayId(id);
                              setOverlayChoice('own');
                            }
                          },
                          'image/png',
                          defaultOverlayId
                        )}
                      </>
                    )}

                    <button
                      type="button"
                      onClick={handleOpenOverlayBuilder}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                    >
                      <Sparkles className="h-3.5 w-3.5" /> Neues Overlay erstellen
                    </button>

                    <p className="text-[11px] text-muted-foreground">
                      PNG mit Transparenz, gleiches Format wie die Bilder ({aspect}).
                    </p>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="watermark">
              <AccordionTrigger className="text-sm font-semibold">
                <span className="flex items-center gap-2">
                  <Droplets className="h-4 w-4 text-primary" /> Wasserzeichen
                  {selectedWatermark && <Badge variant="secondary" className="text-[10px]">aktiv</Badge>}
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                {renderAssetPicker('watermark', watermarkAssets, selectedWatermarkId, setSelectedWatermarkId, 'image/png,image/jpeg')}
                {selectedWatermark && (
                  <>
                    <div>
                      <div className="mb-1.5 flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Deckkraft</Label>
                        <span className="font-mono text-xs">{watermarkOpacity}%</span>
                      </div>
                      <Slider min={5} max={80} step={5} value={[watermarkOpacity]} onValueChange={([v]) => setWatermarkOpacity(v)} />
                    </div>
                    <div>
                      <div className="mb-1.5 flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Größe</Label>
                        <span className="font-mono text-xs">{Math.round(watermarkScale * 100)}%</span>
                      </div>
                      <Slider min={10} max={100} step={5} value={[watermarkScale * 100]} onValueChange={([v]) => setWatermarkScale(v / 100)} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Position</Label>
                      <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                        {watermarkPositionButtons.map(({ position, icon, label }) => (
                          <button
                            key={position}
                            type="button"
                            onClick={() => setWatermarkPosition(position)}
                            className={cn(
                              'flex flex-col items-center gap-0.5 rounded-lg border-2 py-1.5 text-[10px] transition-colors',
                              watermarkPosition === position
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-border text-muted-foreground hover:border-primary/40'
                            )}
                          >
                            {icon}
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Weiß wird transparent, Schwarz behält die Deckkraft.</p>
                  </>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="export" className="border-b-0">
              <AccordionTrigger className="text-sm font-semibold">
                <span className="flex items-center gap-2">
                  <Download className="h-4 w-4 text-primary" /> Export
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-2">
                {(
                  [
                    { value: false, label: 'Volle Qualität', hint: 'Maximale Auflösung (PNG)' },
                    { value: true, label: 'Web-optimiert', hint: 'Komprimiert für Portale & Web (JPG)' },
                  ] as const
                ).map(({ value, label, hint }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setExportCompressed(value)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg border-2 p-2.5 text-left transition-all',
                      exportCompressed === value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                    )}
                  >
                    <span
                      className={cn(
                        'h-4 w-4 flex-shrink-0 rounded-full border-2',
                        exportCompressed === value ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                      )}
                    />
                    <span>
                      <span className="block text-sm font-medium text-foreground">{label}</span>
                      <span className="block text-xs text-muted-foreground">{hint}</span>
                    </span>
                  </button>
                ))}
                <AiLabelOption {...aiLabel} className="mt-1" />
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Rechte Spalte: Live-Vorschau (sticky, erste Fold) */}
          <div className="sticky top-[190px] self-start rounded-xl border border-border bg-card p-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Live-Vorschau ({aspect})
            </p>
            {previewBase ? (
              <div
                className={cn(
                  'relative mx-auto w-full max-w-xl overflow-hidden rounded-lg border border-border bg-muted',
                  aspect === '1:1' ? 'aspect-square' : 'aspect-[4/3]'
                )}
                style={{ containerType: 'inline-size' }}
              >
                <img src={previewBase} alt="Vorschau" className="absolute inset-0 z-0 h-full w-full object-cover" />
                {/* Wasserzeichen-Ebene */}
                {watermarkPreviewUrl && selectedWatermark && (
                  <div
                    className="pointer-events-none absolute inset-0 z-[5] flex items-center justify-center"
                    style={{
                      ...(watermarkPosition === 'tile' && {
                        backgroundImage: `url(${watermarkPreviewUrl})`,
                        backgroundRepeat: 'repeat',
                        backgroundSize: `${watermarkScale * 50}%`,
                      }),
                    }}
                  >
                    {watermarkPosition !== 'tile' && (
                      <img
                        src={watermarkPreviewUrl}
                        alt=""
                        style={{
                          width: `${watermarkScale * 50}%`,
                          position: 'absolute',
                          ...(watermarkPosition === 'center' && { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }),
                          ...(watermarkPosition === 'tl' && { top: '5%', left: '5%' }),
                          ...(watermarkPosition === 'tr' && { top: '5%', right: '5%' }),
                          ...(watermarkPosition === 'bl' && { bottom: '5%', left: '5%' }),
                          ...(watermarkPosition === 'br' && { bottom: '5%', right: '5%' }),
                        }}
                      />
                    )}
                  </div>
                )}
                {/* Overlay-Ebene */}
                {overlayChoice === 'own' && selectedOverlay && (
                  <img src={selectedOverlay.publicUrl} alt="" className="pointer-events-none absolute inset-0 z-10 h-full w-full" />
                )}
                {/* Logo-Ebene */}
                {selectedLogo && (
                  <div
                    className={cn(
                      'pointer-events-none absolute z-20 w-[15%] transition-all duration-300',
                      logoPosition === 'top-left' && 'left-[3%] top-[3%]',
                      logoPosition === 'top-right' && 'right-[3%] top-[3%]',
                      logoPosition === 'bottom-left' && 'bottom-[3%] left-[3%]',
                      logoPosition === 'bottom-right' && 'bottom-[3%] right-[3%]'
                    )}
                  >
                    <img src={selectedLogo.publicUrl} alt="" className="h-auto w-full drop-shadow-lg" />
                  </div>
                )}
                {/* KI-Hinweis-Ebene — zuoberst, wie beim Export. Die Maße
                    spiegeln aiLabelMetrics (Schrift 1,8 %, Rand 1,5 % der
                    Breite, Innenabstand 0.75em/0.5em) über Container-Queries,
                    damit die Vorschau maßstabsgetreu bleibt. */}
                {aiLabel.enabled && (
                  <div
                    className={cn(
                      'pointer-events-none absolute z-30 whitespace-nowrap rounded-full font-medium text-white',
                      aiLabel.position === 'top-left' && 'left-[1.5cqw] top-[1.5cqw]',
                      aiLabel.position === 'top-right' && 'right-[1.5cqw] top-[1.5cqw]',
                      aiLabel.position === 'bottom-left' && 'bottom-[1.5cqw] left-[1.5cqw]',
                      aiLabel.position === 'bottom-right' && 'bottom-[1.5cqw] right-[1.5cqw]'
                    )}
                    style={{
                      fontSize: '1.8cqw',
                      padding: '0.5em 0.75em',
                      backgroundColor: 'rgba(17, 17, 17, 0.55)',
                      opacity: aiLabel.opacity,
                    }}
                  >
                    Mit KI erstellt
                  </div>
                )}
              </div>
            ) : (
              <div className="flex aspect-[4/3] items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground">
                Noch keine generierten Bilder vorhanden.
              </div>
            )}
            <p className="mt-3 text-center text-xs text-muted-foreground">
              Logo, Wasserzeichen, Overlay{aiLabel.enabled ? ' und der KI-Hinweis' : ''} werden beim Download in die finalen Bilder eingebrannt.
            </p>
          </div>
        </div>

        {renderNotice()}

        <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
          <Button variant="ghost" onClick={() => setStep(3)} className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Zurück
          </Button>
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() =>
                void (async () => {
                  if (!project) return;
                  // Erst gebrandete Varianten persistieren — bei Fehler bleiben
                  // wir hier (Notice zeigt das Problem, erneuter Klick = Retry).
                  const ok = await persistBrandedImages();
                  if (ok) onExit('workspace', project.id);
                })()
              }
              disabled={!project || isSavingBranding}
              className="gap-2"
            >
              {isSavingBranding ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Branding wird gespeichert…
                </>
              ) : (
                'Zum Projekt'
              )}
            </Button>
            <Button
              onClick={() => void handleDownload()}
              disabled={downloadFileCount === 0 || isDownloading || isSavingBranding || overlayAssets === null}
              className="gap-2"
            >
              {isDownloading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> ZIP wird erstellt…
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" /> Herunterladen ({downloadFileCount})
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // ==========================================================================
  // Layout
  // ==========================================================================

  const headerTitle =
    mode === 'new' ? (project?.name || projectName.trim() || 'Neues Projekt') : project?.name ?? 'Bilder hinzufügen';

  return (
    <div className="flex min-h-[calc(100vh-60px)] w-full flex-col bg-background">
      {/* Kopfzeile: Schritt-Leiste in voller Breite. Klebt unter der App-TopBar
          (h-[60px]); z unter der TopBar (z-30), damit deren Guthaben-Tooltips
          nicht verdeckt werden, aber über dem scrollenden Inhalt. */}
      <header className="sticky top-[60px] z-20 border-b border-border bg-card">
        <div className="mx-auto max-w-6xl px-4 pb-3 pt-4 sm:px-6">
          {renderStepIndicator()}
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <img src="/logo.png" alt="" className="h-5 w-5 flex-shrink-0 rounded" />
              <span className="truncate text-sm font-bold text-foreground">{headerTitle}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => void handleSaveAndClose()} className="gap-2">
              <Save className="h-4 w-4" /> Speichern &amp; Schließen
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
      </main>

      {/* Geteiltes Prüf-Popup */}
      {project && (
        <ImageInspectorModal
          open={inspectorOpen}
          rooms={inspectorRooms}
          index={inspectorIndex}
          projectId={project.id}
          stagingStyle={style}
          aspectRatio={aspect}
          isAdmin={isAdmin}
          context="wizard"
          onIndexChange={setInspectorIndex}
          onClose={() => setInspectorOpen(false)}
          onChanged={() => void handleInspectorChanged()}
          onCreditsRefresh={onCreditsRefresh}
        />
      )}
    </div>
  );
};
